import { ControlCodes } from '@bcube/shared/constants';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Eye } from 'lucide-react';
import { type ComponentProps, useState } from 'react';
import {
  type FinancialObligationStatus,
  financialObligationApi,
  type PaymentConfirmationObligation,
} from '@/api/commercial/financial-obligations';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { usePermissions } from '@/hooks/use-permissions';

const statusLabels: Record<FinancialObligationStatus, string> = {
  PENDING_PAYMENT: 'Aguardando pagamento',
  PENDING_VALIDATION: 'Aguardando validação',
  APPROVED: 'Aprovado',
  CANCELED: 'Cancelado',
  INTEGRATION_RETRY: 'Integração pendente',
};

const statusVariants: Record<FinancialObligationStatus, ComponentProps<typeof Badge>['variant']> = {
  PENDING_PAYMENT: 'secondary-subtle',
  PENDING_VALIDATION: 'warning-subtle',
  APPROVED: 'success-subtle',
  CANCELED: 'destructive-subtle',
  INTEGRATION_RETRY: 'info-subtle',
};

const VALIDATABLE_STATUSES: FinancialObligationStatus[] = ['PENDING_PAYMENT', 'PENDING_VALIDATION'];
const CANCELLABLE_STATUSES: FinancialObligationStatus[] = [
  ...VALIDATABLE_STATUSES,
  'INTEGRATION_RETRY',
];

export function FinancialReceiptTable({ data }: { data: PaymentConfirmationObligation[] }) {
  const client = useQueryClient();
  const { hasPermission } = usePermissions();
  const canApprove = hasPermission(ControlCodes.financial_payment_confirmation_approve);
  const [canceling, setCanceling] = useState<PaymentConfirmationObligation | null>(null);
  const [reason, setReason] = useState('');
  const cancel = useMutation({
    mutationFn: (id: string) => financialObligationApi.cancel(id, reason.trim()),
    onSuccess: async () => {
      setCanceling(null);
      setReason('');
      await client.invalidateQueries({ queryKey: ['financial-obligations'] });
    },
    onError: () => setError('Não foi possível cancelar a obrigação.'),
  });
  const [selected, setSelected] = useState<PaymentConfirmationObligation | null>(null);
  const [error, setError] = useState<string | null>(null);
  const approve = useMutation({
    mutationFn: (id: string) => financialObligationApi.approve(id),
    onSuccess: async () => {
      setSelected(null);
      await client.invalidateQueries({ queryKey: ['financial-obligations'] });
    },
    onError: () => setError('Não foi possível validar o pagamento.'),
  });
  const viewReceipt = async (id: string) => {
    setError(null);
    try {
      const { url } = await financialObligationApi.getReceiptDownloadUrl(id);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      setError('Não foi possível abrir o comprovante.');
    }
  };

  return (
    <>
      {error && !selected && !canceling && (
        <p role="alert" className="text-sm text-red-600">
          {error}
        </p>
      )}
      <div className="overflow-x-auto rounded border">
        <table className="w-full text-sm">
          <thead>
            <tr>
              <th className="p-3 text-left">Proposta</th>
              <th className="p-3 text-left">Cliente</th>
              <th className="p-3 text-left">Método</th>
              <th className="p-3 text-left">Valor</th>
              <th className="p-3 text-left">Status</th>
              <th className="p-3 text-left">Data</th>
              <th className="p-3 text-left">Ações</th>
            </tr>
          </thead>
          <tbody>
            {data.map((item) => (
              <tr key={item.obligationId} className="border-t">
                <td className="p-3">#{item.proposalSeq}</td>
                <td className="p-3">{item.clientName}</td>
                <td className="p-3">{item.method === 'PIX' ? 'Pix' : 'Boleto'}</td>
                <td className="p-3">
                  {(item.amountInCents / 100).toLocaleString('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                  })}
                </td>
                <td className="p-3">
                  <Badge variant={statusVariants[item.status]}>{statusLabels[item.status]}</Badge>
                </td>
                <td className="p-3">{new Date(item.updatedAt).toLocaleDateString('pt-BR')}</td>
                <td className="p-3">
                  <div className="flex gap-2">
                    {item.receiptId && (
                      <Button
                        type="button"
                        variant="link"
                        size="sm"
                        className="h-8 px-2"
                        onClick={() => void viewReceipt(item.obligationId)}
                      >
                        <Eye className="mr-1 h-4 w-4" />
                        Visualizar
                      </Button>
                    )}
                    {canApprove && VALIDATABLE_STATUSES.includes(item.status) && (
                      <Button
                        type="button"
                        variant="link"
                        size="sm"
                        className="h-8 px-2"
                        onClick={() => {
                          setError(null);
                          setSelected(item);
                        }}
                      >
                        <CheckCircle2 className="mr-1 h-4 w-4" />
                        Validar
                      </Button>
                    )}
                    {canApprove && CANCELLABLE_STATUSES.includes(item.status) && (
                      <Button
                        type="button"
                        variant="link"
                        size="sm"
                        onClick={() => {
                          setError(null);
                          setReason('');
                          setCanceling(item);
                        }}
                      >
                        Cancelar obrigação
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Dialog
        open={Boolean(selected)}
        onOpenChange={(open) => !open && !approve.isPending && setSelected(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Validar pagamento</DialogTitle>
            <DialogDescription>
              Confirma a validação do pagamento da proposta #{selected?.proposalSeq}?
            </DialogDescription>
          </DialogHeader>
          {error && (
            <p role="alert" className="text-red-600">
              {error}
            </p>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={approve.isPending}
              onClick={() => setSelected(null)}
            >
              Voltar
            </Button>
            <Button
              type="button"
              disabled={!selected || approve.isPending}
              onClick={() => selected && approve.mutate(selected.obligationId)}
            >
              {approve.isPending ? 'Validando...' : 'Confirmar validação'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog
        open={Boolean(canceling)}
        onOpenChange={(open) => !open && !cancel.isPending && setCanceling(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancelar obrigação financeira</DialogTitle>
            <DialogDescription>
              Confirma o cancelamento da obrigação da proposta #{canceling?.proposalSeq}? O boleto
              também será encaminhado para cancelamento.
            </DialogDescription>
          </DialogHeader>
          <label className="space-y-2">
            Motivo
            <textarea
              className="w-full rounded border p-2"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              minLength={3}
              maxLength={500}
              disabled={cancel.isPending}
            />
          </label>
          {error && (
            <p role="alert" className="text-red-600">
              {error}
            </p>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={cancel.isPending}
              onClick={() => setCanceling(null)}
            >
              Voltar
            </Button>
            <Button
              type="button"
              disabled={!canceling || reason.trim().length < 3 || cancel.isPending}
              onClick={() => canceling && cancel.mutate(canceling.obligationId)}
            >
              {cancel.isPending ? 'Cancelando...' : 'Confirmar cancelamento'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
